export interface CurrencyFormat {
  symbol: string;
  position: "prefix" | "suffix";
  decimals: number;
  thousandsSeparator: string;
  decimalSeparator: string;
}

export const defaultCurrencyFormats: Record<string, CurrencyFormat> = {
  USD: {
    symbol: "$",
    position: "prefix",
    decimals: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  EUR: {
    symbol: "€",
    position: "suffix",
    decimals: 2,
    thousandsSeparator: " ",
    decimalSeparator: ",",
  },
  GBP: {
    symbol: "£",
    position: "prefix",
    decimals: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  PLN: {
    symbol: "zł",
    position: "suffix",
    decimals: 2,
    thousandsSeparator: " ",
    decimalSeparator: ",",
  },
  JPY: {
    symbol: "¥",
    position: "prefix",
    decimals: 0,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  CHF: {
    symbol: "CHF",
    position: "prefix",
    decimals: 2,
    thousandsSeparator: "'",
    decimalSeparator: ".",
  },
  CAD: {
    symbol: "C$",
    position: "prefix",
    decimals: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  AUD: {
    symbol: "A$",
    position: "prefix",
    decimals: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  SEK: {
    symbol: "kr",
    position: "suffix",
    decimals: 2,
    thousandsSeparator: " ",
    decimalSeparator: ",",
  },
  NOK: {
    symbol: "kr",
    position: "suffix",
    decimals: 2,
    thousandsSeparator: " ",
    decimalSeparator: ",",
  },
  DKK: {
    symbol: "kr",
    position: "suffix",
    decimals: 2,
    thousandsSeparator: " ",
    decimalSeparator: ",",
  },
  CZK: {
    symbol: "Kč",
    position: "suffix",
    decimals: 2,
    thousandsSeparator: " ",
    decimalSeparator: ",",
  },
  HUF: {
    symbol: "Ft",
    position: "suffix",
    decimals: 0,
    thousandsSeparator: " ",
    decimalSeparator: ",",
  },
  BGN: {
    symbol: "лв",
    position: "suffix",
    decimals: 2,
    thousandsSeparator: " ",
    decimalSeparator: ",",
  },
  RON: {
    symbol: "lei",
    position: "suffix",
    decimals: 2,
    thousandsSeparator: ".",
    decimalSeparator: ",",
  },
  HRK: {
    symbol: "kn",
    position: "suffix",
    decimals: 2,
    thousandsSeparator: ".",
    decimalSeparator: ",",
  },
  ISK: {
    symbol: "kr",
    position: "suffix",
    decimals: 0,
    thousandsSeparator: ".",
    decimalSeparator: ",",
  },
  TRY: {
    symbol: "₺",
    position: "suffix",
    decimals: 2,
    thousandsSeparator: ".",
    decimalSeparator: ",",
  },
  CNY: {
    symbol: "¥",
    position: "prefix",
    decimals: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  INR: {
    symbol: "₹",
    position: "prefix",
    decimals: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
};

export const formatCurrency = (
  amount: number,
  currency: string,
  format?: CurrencyFormat,
  locale?: string,
): string => {
  if (!format) {
    format = defaultCurrencyFormats[currency];

    if (!format && locale) {
      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch {
        format = {
          symbol: currency,
          position: "prefix",
          decimals: 2,
          thousandsSeparator: ",",
          decimalSeparator: ".",
        };
      }
    } else if (!format) {
      format = {
        symbol: currency,
        position: "prefix",
        decimals: 2,
        thousandsSeparator: ",",
        decimalSeparator: ".",
      };
    }
  }

  const parts = amount.toFixed(format.decimals).split(".");
  const integerPart = parts[0];
  const decimalPart = parts[1];

  const integerWithSeparators = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    format.thousandsSeparator,
  );

  const formattedNumber = decimalPart
    ? `${integerWithSeparators}${format.decimalSeparator}${decimalPart}`
    : integerWithSeparators;

  if (format.position === "prefix") {
    return `${format.symbol}${format.symbol.match(/[A-Z]/) ? " " : ""}${formattedNumber}`;
  } else {
    return `${formattedNumber} ${format.symbol}`;
  }
};

export const convertCurrency = (
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rates: Record<string, number>,
): number => {
  if (fromCurrency === toCurrency) return amount;

  const fromRate = rates[fromCurrency] || 1;
  const toRate = rates[toCurrency] || 1;

  const inBaseCurrency = amount / fromRate;
  return inBaseCurrency * toRate;
};

export const getCurrencyDisplay = (currencyCode: string): string => {
  const format = defaultCurrencyFormats[currencyCode];
  if (format) {
    return `${currencyCode} (${format.symbol})`;
  }
  return currencyCode;
};

export const getCurrencySymbol = (currencyCode: string): string => {
  const format = defaultCurrencyFormats[currencyCode];
  return format ? format.symbol : currencyCode;
};

export const parseCurrencyAmount = (
  value: string,
  format?: CurrencyFormat,
): number | null => {
  if (!value || value.trim() === "") return null;

  const defaultFormat: CurrencyFormat = {
    symbol: "",
    position: "prefix",
    decimals: 2,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  };

  const activeFormat = format || defaultFormat;

  let cleanedValue = value.trim();

  if (activeFormat.symbol) {
    cleanedValue = cleanedValue.replace(activeFormat.symbol, "").trim();
  }

  if (activeFormat.thousandsSeparator) {
    const regex = new RegExp(`\\${activeFormat.thousandsSeparator}`, "g");
    cleanedValue = cleanedValue.replace(regex, "");
  }

  if (activeFormat.decimalSeparator !== ".") {
    cleanedValue = cleanedValue.replace(activeFormat.decimalSeparator, ".");
  }

  const parsedValue = parseFloat(cleanedValue);
  return isNaN(parsedValue) ? null : parsedValue;
};

export const formatCurrencyInput = (
  value: string,
  format?: CurrencyFormat,
): string => {
  const amount = parseCurrencyAmount(value, format);
  if (amount === null) return value;

  return formatCurrency(amount, "", format);
};

export const validateCurrencyCode = (code: string): boolean => {
  const validCodes = [
    "USD",
    "EUR",
    "GBP",
    "PLN",
    "JPY",
    "CHF",
    "CAD",
    "AUD",
    "NZD",
    "SEK",
    "NOK",
    "DKK",
    "CZK",
    "HUF",
    "BGN",
    "RON",
    "HRK",
    "ISK",
    "TRY",
    "CNY",
    "INR",
    "KRW",
    "SGD",
    "HKD",
    "MXN",
    "BRL",
    "ARS",
    "CLP",
    "COP",
    "PEN",
    "UYU",
    "ZAR",
    "THB",
    "MYR",
    "IDR",
    "PHP",
    "VND",
    "EGP",
    "ILS",
    "AED",
    "SAR",
    "RUB",
    "UAH",
    "KZT",
    "NGN",
    "GHS",
    "KES",
    "MAD",
    "TND",
    "PKR",
    "BDT",
    "LKR",
  ];

  return validCodes.includes(code.toUpperCase());
};

export const roundCurrency = (amount: number, currency: string): number => {
  const format = defaultCurrencyFormats[currency];
  const decimals = format?.decimals ?? 2;

  return Math.round(amount * Math.pow(10, decimals)) / Math.pow(10, decimals);
};
